import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FaEnvelope, FaHeadset, FaStar, FaUsers, FaArrowRight, FaPhoneAlt, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import logo from "../assets/logo.jpeg";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: "easeOut" } },
};



const SAPEAGLE_API_KEY = "SapeagleerpIH0bhqizXUUiBvzU0qxvYAcfvbz9CevkpYF6xYtfwMW1wabnfV2QAeh8Rn9b54An";
const EMAIL_API = "";
const LEAD_EMAIL_RECIPIENTS = [
  "info@sapeagleerp.com",
  "sapeagleerp@gmail.com",
];
const SALES_PHONE = "9910680809";
const NAME_PATTERN = /^[A-Za-z][A-Za-z .'-]{1,49}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
const DISPOSABLE_EMAIL_DOMAINS = new Set([
  "10minutemail.com",
  "guerrillamail.com",
  "mailinator.com",
  "tempmail.com",
  "yopmail.com",
]);

const normalizePhone = (value) => {
  const digits = value.replace(/\D/g, "");
  return digits.length === 12 && digits.startsWith("91") ? digits.slice(2) : digits;
};

const isRepeatedOrSequentialPhone = (digits) => {
  if (/^(\d)\1{9}$/.test(digits)) return true;

  const ascending = "0123456789";
  const descending = "9876543210";
  return ascending.includes(digits) || descending.includes(digits);
};

const validateLeadForm = ({ name, contact, email }) => {
  const trimmedName = name.trim();
  const trimmedEmail = email.trim().toLowerCase();
  const phoneDigits = normalizePhone(contact);
  const emailDomain = trimmedEmail.split("@")[1];

  if (!NAME_PATTERN.test(trimmedName) || /(.)\1{3,}/i.test(trimmedName.replace(/[ .'-]/g, ""))) {
    return "Please enter a valid name using letters only.";
  }

  if (!/^([6-9]\d{9})$/.test(phoneDigits) || isRepeatedOrSequentialPhone(phoneDigits)) {
    return "Please enter a valid 10-digit Indian mobile number.";
  }

  if (!EMAIL_PATTERN.test(trimmedEmail) || DISPOSABLE_EMAIL_DOMAINS.has(emailDomain)) {
    return "Please enter a valid business email address.";
  }

  return "";
};

const escapeHtml = (value = "") => String(value)
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const buildLeadEmail = ({ name, companyName, contact, email, numberOfUsers, remarks }) => `
  <div style="font-family: Arial, sans-serif; color: #111827; line-height: 1.6;">
    <div style="background: #172554; color: #ffffff; padding: 18px 20px; border-radius: 8px 8px 0 0;">
      <h2 style="margin: 0; font-size: 20px;">New Book Demo Enquiry</h2>
      <p style="margin: 4px 0 0; color: #bfdbfe; font-size: 13px;">SAPEAGLE ERP sales lead</p>
    </div>
    <table style="width: 100%; border-collapse: collapse; font-size: 14px; border: 1px solid #dbeafe;">
      ${[
        ["Name", name],
        ["Company", companyName],
        ["Email", email],
        ["Phone", contact],
        ["Number of Users", numberOfUsers],
        ["Remark", remarks],
      ].map(([label, value]) => `
        <tr>
          <th style="padding: 10px 12px; border: 1px solid #dbeafe; text-align: left; background: #eff6ff; color: #1e3a8a; width: 35%;">${label}</th>
          <td style="padding: 10px 12px; border: 1px solid #dbeafe;">${escapeHtml(value || "-")}</td>
        </tr>
      `).join("")}
    </table>
  </div>
`;

const sendLeadEmail = async (formData) => {
  const emailBody = {
    comId: 1,
    empId: 10475,
    subject: `Book Demo Request - ${formData.name}`,
    body: buildLeadEmail(formData),
    isHtml: true,
    attachmentFileName: "",
    attachmentPath: "",
  };

  const results = await Promise.all(
    LEAD_EMAIL_RECIPIENTS.map(async (toEmail) => {
      try {
        const response = await axios.post(
          EMAIL_API,
          { ...emailBody, toEmail },
          { headers: { "X-API-KEY": SAPEAGLE_API_KEY } }
        );
        return response.data?.success === true;
      } catch (error) {
        console.error(`Email API Error for ${toEmail}:`, error.response?.data || error.message);
        return false;
      }
    })
  );

  return results.some(Boolean);
};

const BookDemo = ({ minimalMobile = false }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [numberOfUsers, setNumberOfUsers] = useState("");
  const [remarks, setRemarks] = useState("");
  const bookDemoAPI = "https://erpapi.sapeagleerp.com/api/Customer/save";
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateLeadForm({ name, contact, email });

    if (validationError) {
      setSubmitStatus({ type: "error", message: validationError });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedCompanyName = companyName.trim();
    const trimmedRemarks = remarks.trim();

    const payload = {
      comId: 1,
      empId: 1,
      customerName: trimmedName,
      customerEmail: trimmedEmail,
      customerContact: normalizePhone(contact),
      address: trimmedCompanyName,
      remarks: `Number of Users: ${numberOfUsers}, Remark: ${trimmedRemarks}`,
    };
    const formData = {
      name: trimmedName,
      companyName: trimmedCompanyName,
      contact: normalizePhone(contact),
      email: trimmedEmail,
      numberOfUsers,
      remarks: trimmedRemarks,
    };

    try {
      const res = await axios.post(
        bookDemoAPI,
        payload,
        {
          headers: {
            "X-API-KEY": "SapeagleerpIH0bhqizXUUiBvzU0qxvYAcfvbz9CevkpYF6xYtfwMW1wabnfV2QAeh8Rn9b54An",
          },
        }
      );

      console.log('API Response:', res.data);
      const emailSent = await sendLeadEmail(formData);

      if (res.data.success === true || emailSent) {
        setSubmitStatus({ type: "success", message: "Request received. Our sales team will contact you shortly." });
        setName("");
        setCompanyName("");
        setContact("");
        setEmail("");
        setNumberOfUsers("");
        setRemarks("");
      } else {
        setSubmitStatus({ type: "error", message: "We could not submit your request. Please try again." });
      }
    } catch (error) {
      console.log('API Error:', error.response?.data || error.message);
      setSubmitStatus({ type: "error", message: error.response?.data?.message || "Submission failed. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      <div className="max-w-6xl w-full grid lg:grid-cols-2 gap-8">
        {minimalMobile && (
          <div className="md:hidden flex justify-center lg:col-span-2">
            <img src={logo} alt="SAPEAGLE ERP Logo" className="h-16 w-auto" />
          </div>
        )}
        
        {/* LEFT PANEL */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className={`${minimalMobile ? "hidden md:flex" : "flex"} order-2 lg:order-1 bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-3xl p-8 md:p-12 flex-col justify-center relative overflow-hidden shadow-2xl`}
        >
          {/* Decorative Elements */}
          <motion.div
            variants={itemVariants}
            className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-blue-500/20 to-transparent rounded-full blur-3xl"
          />
          <motion.div
            variants={itemVariants}
            className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-500/20 to-transparent rounded-full blur-3xl"
          />

          <div className="relative z-10">
            <motion.div variants={itemVariants} className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
                Free Demo
              </span>
            </motion.div>

            <motion.h1
              variants={itemVariants}
              className="text-4xl md:text-5xl font-bold leading-tight mb-2"
            >
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Sapeagleerp
              </span>
            </motion.h1>
            <motion.p variants={itemVariants} className="text-2xl text-gray-300 font-semibold mb-8">
              Private Limited
            </motion.p>

            <motion.div variants={containerVariants} className="space-y-6">
              {[
                { icon: <FaEnvelope />, title: "Email Address", value: "info@sapeagleerp.com", href: "mailto:info@sapeagleerp.com", color: "from-blue-500 to-blue-700" },
                { icon: <FaPhoneAlt />, title: "Sales Team", value: "+91 99106 80809", href: `tel:${SALES_PHONE}`, color: "from-cyan-500 to-cyan-700" },
                { icon: <FaHeadset />, title: "24/7 Support", value: "Always here to help you", color: "from-green-500 to-green-700" },
                { icon: <FaStar />, title: "Premium Quality", value: "5-star customer experience", color: "from-amber-500 to-amber-700" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="flex items-start gap-4"
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
                    <span className="text-white text-xl">{item.icon}</span>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">{item.title}</p>
                    {item.href ? (
                      <a href={item.href} className="font-semibold text-white underline decoration-white/40 underline-offset-4 hover:text-cyan-200 transition-colors">
                        {item.value}
                      </a>
                    ) : (
                      <p className="font-semibold text-white">{item.value}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="mt-10 bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 rounded-2xl p-5 text-center"
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <FaUsers className="text-lg" />
                <span className="font-bold">Trusted by 100+ Companies</span>
              </div>
              <p className="text-sm opacity-80">Join thousands of satisfied clients</p>
            </motion.div>
          </div>
        </motion.div>

        {/* RIGHT PANEL - FORM */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="order-1 lg:order-2 bg-white rounded-3xl p-8 md:p-10 shadow-2xl"
        >
          <motion.div variants={itemVariants} className="text-center mb-8">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
              Book Your Free Demo
            </h2>
            <p className="text-gray-500 mt-2">Fill in your details and we'll contact you shortly</p>
          </motion.div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            <motion.div variants={itemVariants} className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                <motion.input
                  whileFocus={{ scale: 1.01, borderColor: "#3b82f6" }}
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  minLength="2"
                  maxLength="50"
                  pattern="[A-Za-z][A-Za-z .'-]{1,49}"
                  title="Enter a valid name using letters only"
                  className="w-full border-2 border-gray-200 rounded-xl p-3 focus:outline-none focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <motion.input
                  whileFocus={{ scale: 1.01, borderColor: "#3b82f6" }}
                  type="text"
                  placeholder="Your Company"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-xl p-3 focus:outline-none focus:border-blue-500 transition-colors"
                  required
                />
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
                <motion.input
                  whileFocus={{ scale: 1.01, borderColor: "#3b82f6" }}
                  type="tel"
                  placeholder="+91 9876543210"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  inputMode="numeric"
                  maxLength="14"
                  pattern="[+]?[ ]?91[ ]?[6-9][0-9]{9}|[6-9][0-9]{9}"
                  title="Enter a valid 10-digit Indian mobile number"
                  className="w-full border-2 border-gray-200 rounded-xl p-3 focus:outline-none focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <motion.input
                  whileFocus={{ scale: 1.01, borderColor: "#3b82f6" }}
                  type="email"
                  placeholder="john@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength="100"
                  title="Enter a valid business email address"
                  className="w-full border-2 border-gray-200 rounded-xl p-3 focus:outline-none focus:border-blue-500 transition-colors"
                  required
                />
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Users</label>
                <motion.input
                  whileFocus={{ scale: 1.01, borderColor: "#3b82f6" }}
                  type="number"
                  min="1"
                  placeholder="e.g. 50"
                  value={numberOfUsers}
                  onChange={(e) => setNumberOfUsers(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-xl p-3 focus:outline-none focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remark</label>
              <motion.textarea
                whileFocus={{ scale: 1.01, borderColor: "#3b82f6" }}
                rows="3"
                placeholder="Tell us what your team needs"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl p-3 resize-none focus:outline-none focus:border-blue-500 transition-colors"
                required
              />
              </div>
            </motion.div>

            {submitStatus && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                role="status"
                className={`flex items-start gap-2 rounded-xl px-4 py-3 text-sm ${submitStatus.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}
              >
                {submitStatus.type === "success" ? <FaCheckCircle className="mt-0.5 flex-shrink-0" /> : <FaExclamationCircle className="mt-0.5 flex-shrink-0" />}
                <span>{submitStatus.message}</span>
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.button
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg transition-all duration-300 flex items-center justify-center gap-2 ${
                isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:shadow-xl"
              } text-white`}
            >
              {isSubmitting ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                  className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
                />
              ) : (
                <>
                  Submit Request
                  <FaArrowRight className="w-4 h-4" />
                </>
              )}
            </motion.button>
          </form>

          <motion.p variants={itemVariants} className="text-center text-gray-400 text-xs mt-4">
            By submitting, you agree to our Terms & Privacy Policy
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
};

export default BookDemo;
